import * as Sentry from '@sentry/react'
import { identifyPostHogUser, resetPostHog } from './posthog'
import { setMarkerReporter } from './marker'

export interface ClerkUserLike {
  id: string
  primaryEmailAddress: { emailAddress: string } | null
  fullName?: string | null
  publicMetadata: Record<string, unknown>
  organizationMemberships: Array<{ role: string }>
}

/**
 * Attach the signed-in Clerk user to Sentry (setUser). Kept separate from
 * the full-identity helper for callers that only care about Sentry.
 */
export function attachClerkUser(user: ClerkUserLike): void {
  const role = resolveRole(user)
  Sentry.setUser({
    id: user.id,
    email: user.primaryEmailAddress?.emailAddress,
    role,
  })
}

export function clearSentryUser(): void {
  Sentry.setUser(null)
}

/**
 * Identify the signed-in Clerk user across every observability tool that's
 * been initialized — Sentry, PostHog, Marker.io. This is the recommended
 * single-call per-app hook after Clerk's `useUser()` resolves.
 */
export function identifyClerkUser(user: ClerkUserLike): void {
  const role = resolveRole(user)
  const email = user.primaryEmailAddress?.emailAddress
  attachClerkUser(user)
  identifyPostHogUser({ id: user.id, email, role })
  void setMarkerReporter({ email, fullName: user.fullName ?? undefined })
}

/**
 * Clear identity across all three tools on sign-out.
 */
export function clearClerkUser(): void {
  clearSentryUser()
  resetPostHog()
}

function resolveRole(user: ClerkUserLike): 'admin' | 'user' {
  if (user.publicMetadata?.role === 'admin') return 'admin'
  if (user.organizationMemberships.some((m) => m.role === 'org:admin')) return 'admin'
  return 'user'
}
